import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  mustHaveMaxLength,
  mustHaveMinLength,
  mustBeInRange,
} from '@/utils/validx-util'
import { type Task } from 'mobx-task'
import {
  type InventoryItem,
  InventoryItemBookkeepingTypeSpecification,
  InventoryItemConsultationTypeSpecification,
  InventoryItemDefaultTypeSpecification,
  InventoryItemExtensionTypeSpecification,
  type InventoryItemPricing,
  type InventoryItemSupportingItem,
  InventoryItemTaxTypeSpecification,
  type InventoryItemType,
  type InventoryItemTypeSpecification,
} from '@/features/inventory/InventoryItem'
import { ManageSupportingItemsModalFormViewModel } from './ManageSupportingItemsModalFormViewModel'
import type { DocumentType } from '@/features/documents/DocumentType'
import { FeeEditorViewModel } from './FeeEditorViewModel'
import { FlatEditorViewModel } from './FlatEditorViewModel'
import { MarkdownEditorViewModel } from './MarkdownEditorViewModel'
import { MarkupEditorViewModel } from './MarkupEditorViewModel'
import { type InventoryItemPricingEditor } from './inventory-item-pricing-editor'
import { type InventoryItemPricingValues } from '@/features/inventory/inventory-item-pricing-values'
import { ExtensionBillingViewModel } from './ExtensionBillingViewModel'

/**
 * Base View Model for handling Inventory Item operations.
 */
export abstract class InventoryItemDialogViewModelBase {
  /**
   * Holds the maximum lengths for each field.
   */
  private readonly _fieldLengths = {
    name: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
    consultationLength: { min: 1, max: 180 },
  } as const

  readonly modal = new ModalViewModel()

  /**
   * A selected Inventory Item. Used for edit/override operations.
   */
  @observable
  accessor item: InventoryItem | undefined = undefined

  /**
   * The name for the Inventory Item.
   */
  @observable
  accessor name = ''

  /**
   * The description for the Inventory Item.
   */
  @observable
  accessor description = ''

  /**
   * The courtesy quantity for the Inventory Item. This represents how many times
   * the item may be applied before it starts counting towards the job price for
   * the client.
   */
  @observable
  accessor courtesyQuantity = 0

  /**
   * The selected inventory item type specification.
   */
  @observable
  accessor itemType: InventoryItemType = 'Default'

  /**
   * The length of the consultation in minutes.
   */
  @observable
  accessor consultationLength = 0

  /**
   * The client price for the review.
   */
  @observable
  accessor reviewPrice = 0

  /**
   * The pro payout for the review.
   */
  @observable
  accessor reviewPayout = 0

  /**
   * The selected inventory item price type.
   */
  @observable
  accessor itemPriceType: InventoryItemPricing = 'Flat'

  @observable
  accessor supportingItems: InventoryItemSupportingItem[] = []

  /**
   * Whether to hide the inventory item from pro.
   */
  @observable
  accessor hideFromPro = false

  /**
   * Whether the item should be added to the scope at the end of every bookkeeping cycle.
   */
  @observable
  accessor recurring = false

  readonly manageSupportingItemsVm

  /**
   * The various editor view models.
   */
  readonly pricingVms = {
    Fee: new FeeEditorViewModel(),
    Flat: new FlatEditorViewModel(),
    Markup: new MarkupEditorViewModel(),
    Markdown: new MarkdownEditorViewModel(),
  } as const

  readonly extensionVm = new ExtensionBillingViewModel()

  /**
   * Validation context for this view model.
   */
  readonly validation

  protected constructor(
    private readonly _getDocumentTypes: () => DocumentType[],
  ) {
    this.manageSupportingItemsVm = new ManageSupportingItemsModalFormViewModel(
      this._getDocumentTypes,
      () => this.supportingItems,
      this.setSupportingItems,
    )

    this.validation = validationContext(this, {
      name: [
        required('A name is required.'),
        mustHaveMinLength(
          this._fieldLengths.name.min,
          `Name must be between ${this._fieldLengths.name.min} and ${this._fieldLengths.name.max} characters.`,
        ),
        mustHaveMaxLength(
          this._fieldLengths.name.max,
          `Name must be between ${this._fieldLengths.name.min} and ${this._fieldLengths.name.max} characters.`,
        ),
      ],
      description: [
        required('A description is required.'),
        mustHaveMinLength(
          this._fieldLengths.description.min,
          `Description must be between ${this._fieldLengths.description.min} and ${this._fieldLengths.description.max} characters.`,
        ),
        mustHaveMaxLength(
          this._fieldLengths.description.max,
          `Description must be between ${this._fieldLengths.description.min} and ${this._fieldLengths.description.max} characters.`,
        ),
      ],
      courtesyQuantity: [
        required('A courtesy quantity is required.'),
        mustBeInRange(0, Infinity, 'Must be zero or greater.'),
      ],
      consultationLength: [
        (ctx) =>
          this.isConsultation
            ? mustBeInRange(
                this._fieldLengths.consultationLength.min,
                this._fieldLengths.consultationLength.max,
                'The consultation length must be between 1 to 180 minutes.',
              )(ctx)
            : true,
      ],
      reviewPrice: [
        (ctx) =>
          this.isTax
            ? mustBeInRange(
                0,
                Infinity,
                'Review price must be zero or greater.',
              )(ctx)
            : true,
      ],
      reviewPayout: [
        (ctx) =>
          this.isTax
            ? mustBeInRange(
                0,
                Infinity,
                'Review Pro payout must be zero or greater.',
              )(ctx)
            : true,
      ],
    })
  }

  /**
   * Gets the active Pricing View Model for the Inventory Item.
   */
  @computed
  get pricingVm(): InventoryItemPricingEditor {
    return this.pricingVms[this.itemPriceType]
  }

  /**
   * Computes the number of remaining characters for each field
   */
  @computed
  get remainingCharactersIndicator() {
    const makeIndicator = (max: number, current: number) =>
      current !== 0 ? `${max - current} characters remaining` : ''

    return {
      name: makeIndicator(this._fieldLengths.name.max, this.name.length),
      description: makeIndicator(
        this._fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * Whether the selected inventory item type is default.
   */
  @computed
  get isDefault() {
    return this.itemType === 'Default'
  }

  /**
   * Whether the selected inventory item type is bookkeeping.
   */
  @computed
  get isBookkeeping() {
    return this.itemType === 'Bookkeeping'
  }

  /**
   * Whether the selected inventory item type is consultation.
   */
  @computed
  get isConsultation() {
    return this.itemType === 'Consultation'
  }

  /**
   * Whether the selected inventory item type is tax.
   */
  @computed
  get isTax() {
    return this.itemType === 'Tax'
  }

  /**
   * Whether the selected inventory item type is extension.
   */
  @computed
  get isExtension() {
    return this.itemType === 'Extension'
  }

  /**
   * Whether the selected inventory item type requires flat pricing.
   */
  @computed
  get requiresFlatPricing() {
    return !this.isDefault
  }

  /**
   * Whether the selected price type is Flat.
   */
  @computed
  get isFlatPriceType() {
    return this.itemPriceType === 'Flat'
  }

  @computed
  get useRegularPricing() {
    return !this.isExtension
  }

  /**
   * Resets elements involved in the creation process
   */
  @action.bound
  reset() {
    this.name = ''
    this.description = ''
    this.courtesyQuantity = 0
    this.itemPriceType = 'Flat'
    this.supportingItems = []
    this.hideFromPro = false
    this.consultationLength = 0
    this.reviewPrice = 0
    this.reviewPayout = 0
    this.itemType = 'Default'
    this.recurring = false
    this.extensionVm.reset()

    this.submit.reset()
    this.validation.reset()
    this.pricingVm.reset()
  }

  @action.bound
  setInventoryItem(item: InventoryItem) {
    this.item = item
    this.name = item.name
    this.description = item.description
    this.courtesyQuantity = item.courtesyAmount
    this.supportingItems = item.supportingItems
    this.itemPriceType = item.priceType
    this.hideFromPro = item.hidden
    this.itemType = item.typeSpecification.type

    if (
      item.typeSpecification instanceof
      InventoryItemConsultationTypeSpecification
    ) {
      this.consultationLength = item.typeSpecification.lengthInMinutes
    }

    if (
      item.typeSpecification instanceof
      InventoryItemBookkeepingTypeSpecification
    ) {
      this.recurring = item.typeSpecification.recurring
    }

    if (item.typeSpecification instanceof InventoryItemTaxTypeSpecification) {
      this.reviewPrice = item.typeSpecification.reviewPrice
      this.reviewPayout = item.typeSpecification.reviewPayout
    }

    if (
      item.typeSpecification instanceof InventoryItemExtensionTypeSpecification
    ) {
      this.extensionVm.hydrate(item.typeSpecification)
    }

    this.pricingVm.hydrate(inventoryItemToPricingValues(item))
  }

  @action.bound
  setItemType(newItemType: InventoryItemType) {
    this.itemType = newItemType

    // Non-default types currently only support Flat pricing.
    if (this.requiresFlatPricing && !this.isFlatPriceType) {
      this.setItemPriceType('Flat')
    }
  }

  @action.bound
  setItemPriceType(newItemPriceType: InventoryItemPricing) {
    this.itemPriceType = newItemPriceType
    this.validation.reset()
    this.pricingVm.reset()
  }

  @action.bound
  open(item: InventoryItem) {
    this.reset()
    this.setInventoryItem(item)
    this.modal.show()
  }

  @action.bound
  setSupportingItems(supportingItems: InventoryItemSupportingItem[]) {
    this.supportingItems = supportingItems
  }

  @action.bound
  toggleHideFromPro() {
    this.hideFromPro = !this.hideFromPro
  }

  @action.bound
  toggleRecurring() {
    this.recurring = !this.recurring
  }

  @action.bound
  validate(): boolean {
    // Both should run independently.
    const base = this.validation.reset().validate().isValid
    const pricing = this.pricingVm.validate()

    if (this.isExtension) {
      const extension = this.extensionVm.validate()

      return base && pricing && extension
    }

    return base && pricing
  }

  abstract submit: Task<[], Promise<void>>

  getInventoryItemTypeSpecification(): InventoryItemTypeSpecification {
    switch (this.itemType) {
      case 'Default':
        return new InventoryItemDefaultTypeSpecification()
      case 'Bookkeeping':
        return new InventoryItemBookkeepingTypeSpecification(this.recurring)
      case 'Consultation':
        return new InventoryItemConsultationTypeSpecification(
          this.consultationLength,
        )
      case 'Tax':
        return new InventoryItemTaxTypeSpecification(
          this.reviewPrice,
          this.reviewPayout,
        )
      case 'Extension':
        return new InventoryItemExtensionTypeSpecification(
          this.extensionVm.extensionType,
          this.extensionVm.billingType,
          this.extensionVm.billingVm.toValues(),
        )
    }
  }
}

/**
 * Maps from an existing Inventory Item to the values-representation of its pricing.
 */
function inventoryItemToPricingValues(
  item: InventoryItem,
): InventoryItemPricingValues {
  switch (item.priceType) {
    case 'Fee':
      return {
        type: 'Fee',
        fee: item.price,
      }
    case 'Flat':
      return {
        type: 'Flat',
        clientPrice: item.price,
        proPayoutAmount: item.proPrice,
      }
    case 'Markup':
      return {
        type: 'Markup',
        clientMarkupRate: item.price,
        proPayoutMarkupRate: item.proPrice,
      }
    case 'Markdown':
      return {
        type: 'Markdown',
        clientMarkdownRate: item.price,
        proPayoutMarkdownRate: item.proPrice,
      }
  }
}
